import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'

const Container = styled.div`
  grid-area: details;
`

const UpgradedToLabel = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

type LoyaltyUpgradeTarget = { name: string }

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  optionName?: string
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  selectedLoyaltyTargetUpgrade?: LoyaltyUpgradeTarget
  upgradeTitleVariant?: 'default' | 'packageUpgrade'
}

function AccommodationSameRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    optionName,
    stayPayRateTier,
    selectedLoyaltyTargetUpgrade,
    upgradeTitleVariant = 'default',
  } = props

  const intl = useIntl()

  const targetUpgradeName = selectedLoyaltyTargetUpgrade?.name
  const hasLoyaltyUpgrade = !!targetUpgradeName
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const packageOptionLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'e3D2ed1NEgPjpp9ZFe0m',
          defaultMessage: 'Option {number}',
          description: 'Label showing the package option number in the same-room option banner.',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = hasLoyaltyUpgrade
    ? intl.formatMessage(
        {
          id: 'yJdvrOHnwCY8Ovx0A9dC',
          defaultMessage: '{tierName} member upgrade',
          description: 'Label indicating that this option includes a loyalty member upgrade (same-room banner).',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2-S47pPpwvcaeurwALuu',
          defaultMessage: 'Upgraded to {upgradeName}',
          description: 'Label indicating the selected loyalty upgrade package name (same-room banner).',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const optionNameLabel = optionName && stayPayRateTier
    ? intl.formatMessage(
        {
          id: '_zgzlQC7jhOJIxFWViD4',
          defaultMessage: '{name}, {count, plural, one {# night free} other {# nights free}}',
          description: 'Package name with included free nights suffix in same-room option banner.',
        },
        { name: optionName, count: stayPayRateTier.freeNights },
      )
    : optionName

  const optionWithUpgradeContent = memberUpgradeLabel && <FormattedMessage
    id="gApD-SMkh4jbSeGytB00"
    defaultMessage="{optionLabel} | <bold>{upgradeLabel}</bold>"
    description="Option number and member upgrade label separated by pipe in same-room option banner"
    values={{
      optionLabel: packageOptionLabel,
      upgradeLabel: memberUpgradeLabel,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
    }}
  />

  return <Container>
    <VerticalSpacer gap={12}>
      <VerticalSpacer gap={4}>
        {!!packageNumber && <Caption variant="large">
          {optionWithUpgradeContent || packageOptionLabel}
        </Caption>}
        {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
        {!!upgradedToLabel && upgradeTitleVariant === 'default' && <BodyText variant="large" weight="bold">
          {upgradedToLabel}
        </BodyText>}
        {!!optionName && <BodyText
          variant="large"
          weight={selectedLoyaltyTargetUpgrade && upgradeTitleVariant === 'packageUpgrade' ? 'normal' : 'bold'}
          colour={selectedLoyaltyTargetUpgrade ? 'neutral-four' : 'normal-contrast'}
          strikeThrough={!!selectedLoyaltyTargetUpgrade}
        >
          {optionNameLabel}
        </BodyText>}
        {!!selectedLoyaltyTargetUpgrade?.name && upgradeTitleVariant === 'packageUpgrade' && <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="SFriXD2my7zJE8o8IGBY"
            defaultMessage="<accent>Package upgraded to:</accent> {upgradeName}"
            description="Applied loyalty package upgrade heading on the property offer page rate tile, naming the upgraded package"
            values={{
              upgradeName: selectedLoyaltyTargetUpgrade.name,
              accent: (chunks: React.ReactNode) => <UpgradedToLabel
                as="span"
                variant="large"
                weight="bold"
                className={`tier-${displayTier}`}
              >
                {chunks}
              </UpgradedToLabel>,
            }}
          />
        </BodyText>}
      </VerticalSpacer>
      {children}
    </VerticalSpacer>
  </Container>
}

export default AccommodationSameRoomOptionTileDetails
